<template>
  <div
    :class="[
      $style.component,
      fixedAspectRatio && $style.fixedAspectRatio
    ]"
    :style="backgroundStyle"
  >
    <StartScreen
      v-if="stage === stages.START"
      v-bind="start"
      @start="stage = stages.GAME"
    />
    <template v-else-if="stage === stages.GAME">
      <div :class="$style.maxWidthContainer">
        <div :class="$style.outerContainer">
          <div :class="$style.innerContainer">
            <Component
              v-bind="$attrs"
              :is="gameComponent"
              @finish="onFinishGame"
            />
            <div
              v-if="hasTimer"
              :class="$style.timerContainer"
            >
              <GameTimer
                ref="timerRef"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
    <EndScreen
      v-else-if="stage === stages.END"
      v-bind="end"
      :stars="starRating"
      :time="timeRating"
      @end="$emit('finish')"
      @retry="onRetry"
    />
  </div>
</template>

<script>
import {computed, ref} from '@vue/composition-api'

import EndScreen from '@/components/EndScreen'
import GameTimer from '@/components/GameTimer'
import StartScreen from '@/components/StartScreen'
import * as localStorage from '@/utils/local-storage'

const BACKGROUND_SHORTHANDS = {
  'gradient-01': 'transparent linear-gradient(180deg, #5B4E98 0%, #7C7FAC 36%, #94A3BB 76%, #B9E5D6 100%) 0% 0% no-repeat padding-box',
  'gradient-02': 'transparent linear-gradient(180deg, #5B4E98 0%, #F59DB2 100%) 0% 0% no-repeat padding-box',
  'gradient-03': 'transparent linear-gradient(180deg, #FE9EB9 0%, #FFF39E 100%) 0% 0% no-repeat padding-box',
  'gradient-04': 'transparent linear-gradient(180deg, #78D2B9 0%, #FDF9A0 100%) 0% 0% no-repeat padding-box',
  'gradient-05': 'transparent linear-gradient(180deg, #8E85BC 0%, #B9E5D6 100%) 0% 0% no-repeat padding-box',
  'gradient-06': 'transparent linear-gradient(180deg, #009FE3 0%, #73CADB 51%, #B9E5D6 100%) 0% 0% no-repeat padding-box',
  'gradient-07': 'transparent linear-gradient(180deg, #756BA4 0%, #FFF58A 100%) 0% 0% no-repeat padding-box',
  'gradient-08': 'transparent linear-gradient(180deg, #FFA8C1 0%, #DAECF5 100%) 0% 0% no-repeat padding-box',
}

const stages = {
  START: Symbol('start'),
  GAME: Symbol('game'),
  END: Symbol('end'),
}

export default {
  props: {
    gameId: {type: String, required: true},
    background: {type: String, required: false},
    start: {type: Object, required: false},
    end: {type: Object, required: false},
    gameComponent: {type: String, required: true},
    hasTimer: {type: Boolean, required: false, default: false},
    fixedAspectRatio: {type: Boolean, required: false, default: true},
    // Some games such as Riddle needs a full page reload. This is because the iframe should not be removed from the dom.
    reloadOnRetry: {type: Boolean, required: false, default: false},
  },
  setup (props) {
    const timerRef = ref()
    const stage = ref(props.start ? stages.START : stages.GAME)
    const starRating = ref()
    const timeRating = ref()
    return {
      stage,
      stages,
      starRating,
      timeRating,
      timerRef,
      onFinishGame ({stars} = {}) {
        const time = (timerRef.value && timerRef.value.getTime()) || undefined
        starRating.value = stars
        timeRating.value = time
        stage.value = stages.END
        const bestStarsStr = localStorage.getGameItem(props.gameId, 'stars')
        const bestStars = typeof bestStarsStr === 'string' ? Number(bestStarsStr) : undefined
        const bestTimeStr = localStorage.getGameItem(props.gameId, 'time')
        const bestTime = typeof bestTimeStr === 'string' ? Number(bestTimeStr) : undefined

        if (!bestStars || bestStars < stars) {
          localStorage.setGameItem(props.gameId, 'stars', stars)
          localStorage.setGameItem(props.gameId, 'time', time)
        } else if (bestStars === stars) {
          if (!bestTime || bestTime > time) {
            localStorage.setGameItem(props.gameId, 'time', time)
          }
        }
      },
      onRetry () {
        starRating.value = undefined
        timeRating.value = undefined

        if (props.reloadOnRetry) {
          window.location.reload()
        } else {
          stage.value = stages.GAME
        }
      },
      backgroundStyle: computed(() => {
        if (!props.background) return
        return {
          background: BACKGROUND_SHORTHANDS[props.background] || props.background,
        }
      }),
    }
  },
  components: {
    EndScreen,
    GameTimer,
    StartScreen,
  },
}
</script>

<style module lang="scss">
.component {
  display: flex;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  min-height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
}

.maxWidthContainer {
  position: relative;
  width: 100%;
  max-width: variables.$breakpoint-1024;
}

.outerContainer {
  box-sizing: border-box;
  position: absolute;
  width: 100%;
  height: 100%;

  .fixedAspectRatio & {
    height: 0;
    padding-bottom: #{(1366 / 1024) * 100%};
    padding-bottom: min(#{(1366 / 1024) * 100%}, 100vh)
  }
}

.innerContainer {
  box-sizing: border-box;
  width: 100%;
  max-height: 100vh;
  padding-top: 82px;
  padding-left: variables.$spacing-16;
  padding-right: variables.$spacing-16;
  padding-bottom: variables.$spacing-16;

  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
}

.timerContainer {
  position: fixed;
  right: variables.$spacing-16;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  width: 100%;
  pointer-events: none;
  z-index: 98;
}
</style>
